import { useEffect, useState } from "react";
import { FocusTrap } from 'focus-trap-react';
import { Dropdown, IDropdownOption, IDropdownStyles, ResponsiveMode, Stack, StackItem } from "@fluentui/react";
import MediaQuery from "react-responsive";
import SlidingPane from "react-sliding-pane";
import classnames from 'classnames';
import { ExpandedSections } from "../../api";
import { getTranslatedText } from "../GoogleTranslate/TranslationStrings";
import { CloseIcon } from "../../assets/closeIcon";
import { chevronRightIcon } from "../../assets/chevronRightIcon";
import { ExpandIcon } from "../../assets/expand";
import { MinimizeIcon } from "../../assets/minimize";
import chatStyles from "../../pages/chat/Chat.module.css";
import styles from "./ProfilePanel.module.css";

const closeX = classnames(chatStyles.spActionTitle);

// Google Translate returns single words lowercased in some languages
const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

const profileDropdownStyles: Partial<IDropdownStyles> = {
    root: {
        border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
        borderRadius: '8px',
        width: '100%',
    },
    dropdown: {
        fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
        fontFamily: 'var(--YW_Theme_Font_Type)',
        borderRadius: '8px',
        border: 'none',
        selectors: {
            ':focus': {
                border: 'none',
                outline: 'none'
            },
            ':focus:after': {
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
                borderRadius: '8px'
            }
        }
    },
    dropdownItem: {
        fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
        fontFamily: 'var(--YW_Theme_Font_Type)',
        borderRadius: '8px',
        border: 'none',
        selectors: {
            '&:hover': {
                backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
                color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)'
            },
            '&:focus': {
                backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
                color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)',
                outline: 'none'
            },
            ':focus-within': {
                outline: 'none',
                border: 'none'
            }
        }
    },
    dropdownItemSelected: {
        fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
        fontFamily: 'var(--YW_Theme_Font_Type)',
        backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
        color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)',
        border: 'none',
        borderRadius: '8px',
        selectors: {
            '&:hover': {
                backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
                color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)',
                border: 'none'
            },
            '&:focus': {
                border: 'none',
                color: 'none'
            }
        }
    },
    title: {
        fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
        fontFamily: 'var(--YW_Theme_Font_Type)',
        border: 'none',
        borderRadius: '8px',
        selectors: {
            '&:focus': {
                outline: 'none !important',
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
                boxShadow: 'none !important'
            },
            '&:focus:after': {
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
                borderRadius: '8px'
            },
            '&:active': {
                outline: 'none !important',
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
                color: 'inherit !important'
            },
            '&:hover': {
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important'
            }
        }
    },
    caretDown: {
        color: 'var(--YW_SignIn_Modal_SwitchCase_Dropdown_Caret_Color)',
    },
};

type ActionHandlers = Record<string, () => void>;

interface ProfilePanelProps {
    isProfilePaneOpen: boolean;
    setIsProfilePaneOpen: (open: boolean) => void;
    profilePaneData: any;
    selectedProfileSection: string | null;
    setSelectedProfileSection: (section: string) => void;
    expandedProfileSections: ExpandedSections;
    isDarkMode: boolean;
    popUpBlocker: boolean;
    isFocusTrapPaused: boolean;
    caseSelectDialogOpen: boolean;
    addCaseDialogOpen: boolean;
    setIsOtherFocusTrapOpen: (open: boolean) => void;
    setPopUpBlocker: (blocked: boolean) => void;
    setQuestionV: (val: string) => void;
    toggleProfileSection: (sectionId: string) => void;
    isProfileDataLoaded: () => boolean;
    actionHandlers: ActionHandlers;
    showSwitchCases: boolean;
    hasInternalPrograms?: boolean;
    onExternalProgramSelect?: (programCode: string) => void;
    onExternalProgramRemoveRequest?: (programCode: string, programLabel: string) => void;
    onCaseRemoveRequest?: (caseNumber: string, caseLabel: string) => void;
}

const ProfilePanel = ({
    isProfilePaneOpen,
    setIsProfilePaneOpen,
    profilePaneData,
    selectedProfileSection,
    setSelectedProfileSection,
    expandedProfileSections,
    isDarkMode,
    popUpBlocker,
    isFocusTrapPaused,
    caseSelectDialogOpen,
    addCaseDialogOpen,
    setIsOtherFocusTrapOpen,
    setPopUpBlocker,
    setQuestionV,
    toggleProfileSection,
    isProfileDataLoaded,
    actionHandlers,
    showSwitchCases,
    hasInternalPrograms = false,
    onExternalProgramSelect,
    onExternalProgramRemoveRequest,
    onCaseRemoveRequest,
}: ProfilePanelProps) => {
    const [editingSubsection, setEditingSubsection] = useState<string | null>(null);
    const [isPanelFocused, setIsPanelFocused] = useState(false);

    useEffect(() => {
        setEditingSubsection(null);
    }, [selectedProfileSection]);

    useEffect(() => {
        if (!isProfilePaneOpen) setIsPanelFocused(false);
    }, [isProfilePaneOpen]);

    const subsectionHasRemovable = (sectionTitle: string, subsectionTitle: string): boolean => {
        const data = profilePaneData?.subsection_data?.[sectionTitle];
        if (!Array.isArray(data)) return false;
        const subsection = data.find((s: any) => String(s.subsection_title) === subsectionTitle);
        if (!subsection || !Array.isArray(subsection.items)) return false;
        return subsection.items.some((item: any) => item.removable === true && (item.program_code || item.case_number));
    };

    const getVisibleFooterItems = (subsectionTitle: string): { key: string; value: string }[] => {
        const items = profilePaneData?.subsection_data?.["Footer"]?.find((subsection: any) => subsection.subsection_title === subsectionTitle)?.items || [];
        return items.filter((item: { key: string; value: string }) => item.value !== "handleCaseSelectDialogOpen" || showSwitchCases);
    };

    const displayFooterSection = (): boolean => {
        return isProfileDataLoaded() && (
            (!selectedProfileSection && getVisibleFooterItems("Default Actions").length > 0) ||
            (!!selectedProfileSection && getVisibleFooterItems("Secondary Actions").length > 0)
        );
    };

    const profileSections = (profilePaneData.sections || []).filter(
        (sectionTitle: string) => !sectionTitle.includes("Footer") && (!hasInternalPrograms || sectionTitle !== "Programs")
    );

    const getDropdownOptions = (value: string | { label: string; value: string }[]): IDropdownOption[] => (
        Array.isArray(value)
            ? value.map((opt) => ({ key: opt.value, text: opt.label }))
            : []
    );

    const handleDropdownChange = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option?.key && onExternalProgramSelect) {
            onExternalProgramSelect(String(option.key));
        }
    };

    return (
        <>
            <MediaQuery query="(min-width: 992px)">
                {/* Kept mounted (hidden via visibility) while closed so Google Translate
                    picks the panel up in its page-wide pass on language change — some
                    browsers won't translate content that mounts after that pass. */}
                {isProfileDataLoaded() && (
                    <Stack
                        key={`profile-pane-${selectedProfileSection || 'root'}`}
                        horizontal
                        horizontalAlign="end"
                        style={{
                            maxWidth: '25%',
                            marginLeft: '25px',
                            position: 'fixed',
                            zIndex: '98',
                            right: '0',
                            visibility: isProfilePaneOpen ? 'visible' : 'hidden',
                        }}
                        onFocus={() => setIsPanelFocused(true)}
                        onBlur={(e: React.FocusEvent<HTMLElement>) => {
                            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                setIsPanelFocused(false);
                            }
                        }}
                    >
                        <FocusTrap
                            active={isProfilePaneOpen && isPanelFocused && !caseSelectDialogOpen && !addCaseDialogOpen && !isFocusTrapPaused}
                            focusTrapOptions={{
                                clickOutsideDeactivates: true,
                                escapeDeactivates: true,
                                returnFocusOnDeactivate: false,
                                initialFocus: false,
                                fallbackFocus: () => document.getElementById("profile-close") || document.body,
                                onDeactivate: () => { document.getElementById("sign-in-out-icon")?.focus(); setIsOtherFocusTrapOpen(false); },
                                onActivate: () => setIsOtherFocusTrapOpen(true),
                            }}
                        >
                            <div className={isDarkMode ? styles.spProfileContainerDeskTopDM : chatStyles.spContainer}>
                                <Stack
                                    horizontal
                                    verticalAlign="center"
                                    horizontalAlign="start"
                                    className={chatStyles.spTitleContainer}
                                >
                                    <StackItem className={chatStyles.spTitle}>
                                        {selectedProfileSection ? selectedProfileSection : "My Profile"}
                                    </StackItem>
                                    <div
                                        id="profile-close"
                                        onClick={() => {
                                            if (popUpBlocker) return;
                                            if (selectedProfileSection) {
                                                setSelectedProfileSection("");
                                            } else {
                                                setIsProfilePaneOpen(false);
                                                setPopUpBlocker(false);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " " && !popUpBlocker) {
                                                if (selectedProfileSection) {
                                                    setSelectedProfileSection("");
                                                } else {
                                                    setIsProfilePaneOpen(false);
                                                    setPopUpBlocker(false);
                                                }
                                            }
                                        }}
                                        className={closeX}
                                        tabIndex={0}
                                        aria-label={selectedProfileSection ? "Return to Profile Panel" : "Close Profile Panel"}
                                        title={selectedProfileSection ? "Return to Profile Panel" : "Close Profile Panel"}
                                        role="button"
                                        style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                    >
                                        <CloseIcon />
                                    </div>
                                </Stack>
                                {!selectedProfileSection ? (
                                    // Render profile sections
                                    profileSections.map((section: string, idx: number) => (
                                            <Stack
                                                className={chatStyles.spSubsectionContainer}
                                                key={idx}
                                            >
                                                <Stack
                                                    horizontal
                                                    horizontalAlign="space-between"
                                                    verticalAlign="center"
                                                    tabIndex={0}
                                                    role="button"
                                                    aria-label={`Select ${section} Section`}
                                                    onClick={() => {
                                                        if (popUpBlocker) return;
                                                        setSelectedProfileSection(section);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            if (!popUpBlocker) {
                                                                setSelectedProfileSection(section);
                                                            }
                                                        }
                                                    }}
                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <StackItem
                                                        className={chatStyles.spSubsectionTitle}
                                                        title={section}
                                                    >
                                                        {section}
                                                    </StackItem>
                                                    <StackItem
                                                        className={chatStyles.spExpand}
                                                        aria-hidden="true"
                                                        style={{
                                                            cursor: popUpBlocker ? 'not-allowed' : 'pointer',
                                                        }}
                                                    >
                                                        {chevronRightIcon}
                                                    </StackItem>
                                                </Stack>
                                            </Stack>
                                        ))
                                ) : (
                                    // Render selected section with its subsections
                                    (selectedProfileSection &&
                                        (profilePaneData.subsections[selectedProfileSection]
                                            .map((subsectionTitle: string) => (
                                                <Stack
                                                    className={chatStyles.spSubsectionContainer}
                                                    key={`${selectedProfileSection}|${subsectionTitle}`}
                                                >
                                                    <Stack
                                                        horizontal
                                                        horizontalAlign="space-between"
                                                        verticalAlign="center"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            if (popUpBlocker) return;
                                                            toggleProfileSection(`${selectedProfileSection}|${subsectionTitle}`);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (!popUpBlocker) {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    toggleProfileSection(`${selectedProfileSection}|${subsectionTitle}`);
                                                                }
                                                            }
                                                        }}
                                                        style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                    >
                                                        <StackItem
                                                            className={chatStyles.spSubsectionTitle}
                                                            title={subsectionTitle}
                                                        >
                                                            {subsectionTitle}
                                                        </StackItem>
                                                        <div className={styles.spSubsectionHeaderEnd}>
                                                            {subsectionHasRemovable(selectedProfileSection, subsectionTitle) && (
                                                                <span
                                                                    translate="no"
                                                                    role="button"
                                                                    tabIndex={popUpBlocker ? -1 : 0}
                                                                    aria-label={editingSubsection === subsectionTitle ? `Done editing ${subsectionTitle}` : `Edit ${subsectionTitle}`}
                                                                    title={editingSubsection === subsectionTitle ? capitalize(getTranslatedText('done') || "Done") : capitalize(getTranslatedText('edit') || "Edit")}
                                                                    className={styles.spEditToggle}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (popUpBlocker) return;
                                                                        setEditingSubsection(editingSubsection === subsectionTitle ? null : subsectionTitle);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            e.stopPropagation();
                                                                            if (popUpBlocker) return;
                                                                            setEditingSubsection(editingSubsection === subsectionTitle ? null : subsectionTitle);
                                                                        }
                                                                    }}
                                                                >
                                                                    {editingSubsection === subsectionTitle ? `[${capitalize(getTranslatedText('done') || "Done")}]` : `[${capitalize(getTranslatedText('edit') || "Edit")}]`}
                                                                </span>
                                                            )}
                                                            <span
                                                                className={`${chatStyles.spExpand} ${styles.spHeaderExpand}`}
                                                                role="button"
                                                                style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                                title="Expand/Collapse section"
                                                                aria-label="Expand/Collapse section"
                                                            >
                                                                {expandedProfileSections[`${selectedProfileSection}|${subsectionTitle}`] ? MinimizeIcon : ExpandIcon}
                                                            </span>
                                                        </div>
                                                    </Stack>
                                                    {/* Render subsection's inner items and their values*/}
                                                    {expandedProfileSections[`${selectedProfileSection}|${subsectionTitle}`] && (() => {
                                                        const subsectionObj = profilePaneData.subsection_data[selectedProfileSection].find((item: any) => String(item.subsection_title) === subsectionTitle);
                                                        return subsectionObj
                                                            ? subsectionObj.items.map((kv: { key: string; value: string | { label: string; value: string }[]; type?: string; program_code?: string; case_number?: string; removable?: boolean }, itemIdx: number) => (
                                                                <Stack
                                                                    key={itemIdx}
                                                                    tabIndex={kv.type === "dropdown" || kv.type === "assigned_program" || kv.type === "assigned_case" ? undefined : 0}
                                                                    horizontal
                                                                    verticalAlign="center"
                                                                    horizontalAlign="start"
                                                                    className={styles.spProfileValContainer}
                                                                >
                                                                    {kv.type === "assigned_program" ? (
                                                                        <>
                                                                            <StackItem className={styles.spProfileVal} title={kv.key} aria-label={kv.key}>
                                                                                {kv.key}
                                                                            </StackItem>
                                                                            {kv.removable && kv.program_code && onExternalProgramRemoveRequest && editingSubsection === subsectionTitle && (
                                                                                <div
                                                                                    role="button"
                                                                                    tabIndex={popUpBlocker ? -1 : 0}
                                                                                    aria-label={`Remove ${kv.key}`}
                                                                                    title={`Remove ${kv.key}`}
                                                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
                                                                                    onClick={() => {
                                                                                        if (popUpBlocker) return;
                                                                                        onExternalProgramRemoveRequest(kv.program_code!, kv.key);
                                                                                    }}
                                                                                    onKeyDown={(e) => {
                                                                                        if (popUpBlocker) return;
                                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                                            onExternalProgramRemoveRequest(kv.program_code!, kv.key);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <CloseIcon />
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : kv.type === "assigned_case" ? (
                                                                        <>
                                                                            <StackItem className={styles.spProfileVal} title={kv.key} aria-label={kv.key}>
                                                                                {kv.key}
                                                                            </StackItem>
                                                                            <div className={styles.spCaseRowEnd}>
                                                                                <StackItem translate="no" className={styles.spProfileVal} aria-label={kv.value as string} title={kv.value as string}>
                                                                                    {(kv.value as string).length > 20 ? `${(kv.value as string).substring(0, 20)}...` : kv.value as string}
                                                                                </StackItem>
                                                                                {kv.removable && kv.case_number && onCaseRemoveRequest && editingSubsection === subsectionTitle && (
                                                                                    <div
                                                                                        role="button"
                                                                                        tabIndex={popUpBlocker ? -1 : 0}
                                                                                        aria-label={`Remove case ${kv.key}`}
                                                                                        title={`Remove case ${kv.key}`}
                                                                                        style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
                                                                                        onClick={() => {
                                                                                            if (popUpBlocker) return;
                                                                                            onCaseRemoveRequest(kv.case_number!, kv.key);
                                                                                        }}
                                                                                        onKeyDown={(e) => {
                                                                                            if (popUpBlocker) return;
                                                                                            if (e.key === "Enter" || e.key === " ") {
                                                                                                onCaseRemoveRequest(kv.case_number!, kv.key);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <CloseIcon />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    ) : kv.type === "dropdown" ? (
                                                                        <StackItem className={styles.spProfileVal} style={{ width: "100%" }}>
                                                                            <Dropdown
                                                                                aria-label={kv.key}
                                                                                title={kv.key}
                                                                                placeholder={Array.isArray(kv.value) && kv.value.length > 0 ? "Select a program to add" : "There are no additional programs."}
                                                                                selectedKey={null}
                                                                                options={getDropdownOptions(kv.value)}
                                                                                onChange={handleDropdownChange}
                                                                                calloutProps={{ doNotLayer: true }}
                                                                                styles={profileDropdownStyles}
                                                                                responsiveMode={ResponsiveMode.large}
                                                                            />
                                                                        </StackItem>
                                                                    ) : (
                                                                        String(kv.key || "").trim() === "" ? (
                                                                            <StackItem translate="no" className={`${styles.spProfileVal} ${styles.spProfileMessage}`} aria-label={kv.value as string} title={kv.value as string}>
                                                                                {(kv.value as string).length > 20 ? `${(kv.value as string).substring(0, 20)}...` : kv.value as string}
                                                                            </StackItem>
                                                                        ) : (
                                                                            <>
                                                                                <StackItem className={styles.spProfileVal} title={kv.key} aria-label={kv.key}>
                                                                                    {kv.key}
                                                                                </StackItem>
                                                                                <StackItem translate="no" className={styles.spProfileVal} aria-label={kv.value as string} title={kv.value as string}>
                                                                                    {(kv.value as string).length > 20 ? `${(kv.value as string).substring(0, 20)}...` : kv.value as string}
                                                                                </StackItem>
                                                                            </>
                                                                        )
                                                                    )}
                                                                </Stack>
                                                            ))
                                                            : null;
                                                    })()}
                                                </Stack>
                                            ))
                                        )
                                    )
                                )}
                                {/* Only render the footer container if there are items to display */}
                                {displayFooterSection() && (
                                    <Stack className={styles.spFooterContainer}>
                                        {!selectedProfileSection &&
                                            (getVisibleFooterItems("Default Actions").map((kv: { key: string; value: string }, itemIdx: number) => (
                                                <Stack
                                                    key={`default-${itemIdx}`}
                                                    horizontal
                                                    horizontalAlign="space-between"
                                                    verticalAlign="center"
                                                    tabIndex={0}
                                                    className={styles.spFooterTitleContainer}
                                                    onClick={() => {
                                                        if (kv.value === "handleCaseSelectDialogOpen") {
                                                            setQuestionV("");
                                                        }
                                                        actionHandlers[kv.value]?.();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!popUpBlocker) {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                if (kv.value === "handleCaseSelectDialogOpen") {
                                                                    setQuestionV("");
                                                                }
                                                                actionHandlers[kv.value]?.();
                                                            }
                                                        }
                                                    }}
                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <StackItem
                                                        className={chatStyles.spSubsectionTitle}
                                                        title={kv.key}
                                                        aria-label={kv.key}
                                                    >
                                                        {kv.key}
                                                    </StackItem>
                                                </Stack>
                                            ))
                                            )}
                                        {selectedProfileSection &&
                                            (getVisibleFooterItems("Secondary Actions").map((kv: { key: string; value: string }, itemIdx: number) => (
                                                <Stack
                                                    key={`secondary-${itemIdx}`}
                                                    horizontal
                                                    horizontalAlign="space-between"
                                                    verticalAlign="center"
                                                    tabIndex={0}
                                                    className={styles.spFooterTitleContainer}
                                                    onClick={() => {
                                                        if (kv.value === "handleCaseSelectDialogOpen") {
                                                            setQuestionV("");
                                                        }
                                                        actionHandlers[kv.value]?.();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!popUpBlocker) {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                if (kv.value === "handleCaseSelectDialogOpen") {
                                                                    setQuestionV("");
                                                                }
                                                                actionHandlers[kv.value]?.();
                                                            }
                                                        }
                                                    }}
                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <StackItem
                                                        className={chatStyles.spSubsectionTitle}
                                                        title={kv.key}
                                                        aria-label={kv.key}
                                                    >
                                                        {kv.key}
                                                    </StackItem>
                                                </Stack>
                                            ))
                                            )
                                        }
                                    </Stack>
                                )}
                            </div>
                        </FocusTrap>
                    </Stack>
                )}
            </MediaQuery>

            <MediaQuery query="(max-width: 991px)">
                {isProfileDataLoaded() && (
                    <FocusTrap
                        active={isProfilePaneOpen && !caseSelectDialogOpen && !addCaseDialogOpen && !isFocusTrapPaused}
                        focusTrapOptions={{
                            clickOutsideDeactivates: true,
                            escapeDeactivates: true,
                            onActivate: () => setIsOtherFocusTrapOpen(true),
                            onDeactivate: () => setIsOtherFocusTrapOpen(false),
                        }}
                    >
                        <SlidingPane
                            isOpen={isProfilePaneOpen}
                            from="bottom"
                            className={chatStyles.spCustomFAQ}
                            width={"100%"}
                            hideHeader
                            onRequestClose={() => {
                                setIsProfilePaneOpen(false);
                                setPopUpBlocker(false);
                                setTimeout(() => {
                                    document.getElementById("additional-chat-options")?.focus();
                                }, 600);
                            }}
                        >
                            <div className={chatStyles.spContainer}>
                                <Stack
                                    horizontal
                                    verticalAlign="center"
                                    horizontalAlign="start"
                                    className={chatStyles.spTitleContainer}
                                >
                                    <StackItem
                                        key={`profile-title-${selectedProfileSection || 'root'}`}
                                        className={chatStyles.spTitle}
                                    >
                                        {selectedProfileSection ? selectedProfileSection : "My Profile"}
                                    </StackItem>
                                    <div
                                        id="profile-close"
                                        onClick={() => {
                                            if (selectedProfileSection) {
                                                setSelectedProfileSection("");
                                            } else {
                                                setIsProfilePaneOpen(false);
                                                setPopUpBlocker(false);
                                                setTimeout(() => {
                                                    document.getElementById("additional-chat-options")?.focus();
                                                }, 600);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                if (selectedProfileSection) {
                                                    setSelectedProfileSection("");
                                                } else {
                                                    setIsProfilePaneOpen(false);
                                                    setPopUpBlocker(false);
                                                    setTimeout(() => {
                                                        document.getElementById("additional-chat-options")?.focus();
                                                    }, 600);
                                                }
                                            }
                                        }}
                                        className={closeX}
                                        tabIndex={0}
                                        title={selectedProfileSection ? "Return to Profile Panel" : "Close Profile Panel"}
                                        aria-label={selectedProfileSection ? "Return to Profile Panel" : "Close Profile Panel"}
                                        role="button"
                                    >
                                        <CloseIcon fill="var(--YW_Panels_Icons_Color)" />
                                    </div>
                                </Stack>
                                {!selectedProfileSection ?
                                    // Render sections
                                    (profileSections.map((section: string, idx: number) => (
                                            <Stack
                                                className={chatStyles.spSubsectionContainer}
                                                key={idx}
                                            >
                                                <Stack
                                                    horizontal
                                                    horizontalAlign="space-between"
                                                    verticalAlign="center"
                                                    tabIndex={0}
                                                    role="button"
                                                    aria-label={`Select ${section} Section`}
                                                    onClick={() => {
                                                        if (popUpBlocker) return;
                                                        setSelectedProfileSection(section);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            if (!popUpBlocker) {
                                                                setSelectedProfileSection(section);
                                                            }
                                                        }
                                                    }}
                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <StackItem
                                                        className={chatStyles.spSubsectionTitle}
                                                        title={section}
                                                    >
                                                        {section}
                                                    </StackItem>
                                                    <StackItem
                                                        className={chatStyles.spExpand}
                                                        aria-hidden="true"
                                                        style={{
                                                            cursor: popUpBlocker ? 'not-allowed' : 'pointer',
                                                        }}
                                                    >
                                                        {chevronRightIcon}
                                                    </StackItem>
                                                </Stack>
                                            </Stack>
                                        ))
                                    ) : (
                                        // Render selected section with its subsections
                                        selectedProfileSection &&
                                        profilePaneData.subsections[selectedProfileSection]
                                            .map((subsectionTitle: string) => (
                                                <Stack
                                                    className={chatStyles.spSubsectionContainer}
                                                    key={`${selectedProfileSection}|${subsectionTitle}`}
                                                >
                                                    <Stack
                                                        horizontal
                                                        horizontalAlign="space-between"
                                                        verticalAlign="center"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            if (popUpBlocker) return;
                                                            toggleProfileSection(`${selectedProfileSection}|${subsectionTitle}`);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (!popUpBlocker) {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    toggleProfileSection(`${selectedProfileSection}|${subsectionTitle}`);
                                                                }
                                                            }
                                                        }}
                                                        style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                    >
                                                        <StackItem
                                                            className={chatStyles.spSubsectionTitle}
                                                            title={subsectionTitle}
                                                        >
                                                            {subsectionTitle}
                                                        </StackItem>
                                                        <div className={styles.spSubsectionHeaderEnd}>
                                                            {subsectionHasRemovable(selectedProfileSection, subsectionTitle) && (
                                                                <span
                                                                    translate="no"
                                                                    role="button"
                                                                    tabIndex={popUpBlocker ? -1 : 0}
                                                                    aria-label={editingSubsection === subsectionTitle ? `Done editing ${subsectionTitle}` : `Edit ${subsectionTitle}`}
                                                                    title={editingSubsection === subsectionTitle ? capitalize(getTranslatedText('done') || "Done") : capitalize(getTranslatedText('edit') || "Edit")}
                                                                    className={styles.spEditToggle}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (popUpBlocker) return;
                                                                        setEditingSubsection(editingSubsection === subsectionTitle ? null : subsectionTitle);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            e.stopPropagation();
                                                                            if (popUpBlocker) return;
                                                                            setEditingSubsection(editingSubsection === subsectionTitle ? null : subsectionTitle);
                                                                        }
                                                                    }}
                                                                >
                                                                    {editingSubsection === subsectionTitle ? `[${capitalize(getTranslatedText('done') || "Done")}]` : `[${capitalize(getTranslatedText('edit') || "Edit")}]`}
                                                                </span>
                                                            )}
                                                            <span
                                                                className={`${chatStyles.spExpand} ${styles.spHeaderExpand}`}
                                                                role="button"
                                                                style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                                title="Expand/Collapse section"
                                                                aria-label="Expand/Collapse section"
                                                            >
                                                                {expandedProfileSections[`${selectedProfileSection}|${subsectionTitle}`] ? MinimizeIcon : ExpandIcon}
                                                            </span>
                                                        </div>
                                                    </Stack>
                                                    {/* Render subsection's inner items and their values*/}
                                                    {expandedProfileSections[`${selectedProfileSection}|${subsectionTitle}`] && (() => {
                                                        const subsectionObj = profilePaneData.subsection_data[selectedProfileSection].find((item: any) => String(item.subsection_title) === subsectionTitle);
                                                        return subsectionObj
                                                            ? subsectionObj.items.map((kv: { key: string; value: string | { label: string; value: string }[]; type?: string; program_code?: string; case_number?: string; removable?: boolean }, itemIdx: number) => (
                                                                <Stack
                                                                    key={itemIdx}
                                                                    tabIndex={kv.type === "dropdown" || kv.type === "assigned_program" || kv.type === "assigned_case" ? undefined : 0}
                                                                    horizontal
                                                                    verticalAlign="center"
                                                                    horizontalAlign="start"
                                                                    className={styles.spProfileValContainer}
                                                                >
                                                                    {kv.type === "assigned_program" ? (
                                                                        <>
                                                                            <StackItem className={styles.spProfileVal} title={kv.key} aria-label={kv.key}>
                                                                                {kv.key}
                                                                            </StackItem>
                                                                            {kv.removable && kv.program_code && onExternalProgramRemoveRequest && editingSubsection === subsectionTitle && (
                                                                                <div
                                                                                    role="button"
                                                                                    tabIndex={popUpBlocker ? -1 : 0}
                                                                                    aria-label={`Remove ${kv.key}`}
                                                                                    title={`Remove ${kv.key}`}
                                                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
                                                                                    onClick={() => {
                                                                                        if (popUpBlocker) return;
                                                                                        onExternalProgramRemoveRequest(kv.program_code!, kv.key);
                                                                                    }}
                                                                                    onKeyDown={(e) => {
                                                                                        if (popUpBlocker) return;
                                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                                            onExternalProgramRemoveRequest(kv.program_code!, kv.key);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <CloseIcon />
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : kv.type === "assigned_case" ? (
                                                                        <>
                                                                            <StackItem className={styles.spProfileVal} title={kv.key} aria-label={kv.key}>
                                                                                {kv.key}
                                                                            </StackItem>
                                                                            <div className={styles.spCaseRowEnd}>
                                                                                <StackItem translate="no" className={styles.spProfileVal} aria-label={kv.value as string} title={kv.value as string}>
                                                                                    {(kv.value as string).length > 20 ? `${(kv.value as string).substring(0, 20)}...` : kv.value as string}
                                                                                </StackItem>
                                                                                {kv.removable && kv.case_number && onCaseRemoveRequest && editingSubsection === subsectionTitle && (
                                                                                    <div
                                                                                        role="button"
                                                                                        tabIndex={popUpBlocker ? -1 : 0}
                                                                                        aria-label={`Remove case ${kv.key}`}
                                                                                        title={`Remove case ${kv.key}`}
                                                                                        style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
                                                                                        onClick={() => {
                                                                                            if (popUpBlocker) return;
                                                                                            onCaseRemoveRequest(kv.case_number!, kv.key);
                                                                                        }}
                                                                                        onKeyDown={(e) => {
                                                                                            if (popUpBlocker) return;
                                                                                            if (e.key === "Enter" || e.key === " ") {
                                                                                                onCaseRemoveRequest(kv.case_number!, kv.key);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <CloseIcon />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    ) : kv.type === "dropdown" ? (
                                                                        <StackItem className={styles.spProfileVal} style={{ width: "100%" }}>
                                                                            <Dropdown
                                                                                aria-label={kv.key}
                                                                                title={kv.key}
                                                                                placeholder={Array.isArray(kv.value) && kv.value.length > 0 ? "Select a program to add" : "There are no additional programs."}
                                                                                selectedKey={null}
                                                                                options={getDropdownOptions(kv.value)}
                                                                                onChange={handleDropdownChange}
                                                                                calloutProps={{ doNotLayer: true }}
                                                                                styles={profileDropdownStyles}
                                                                                responsiveMode={ResponsiveMode.large}
                                                                            />
                                                                        </StackItem>
                                                                    ) : (
                                                                        String(kv.key || "").trim() === "" ? (
                                                                            <StackItem translate="no" className={`${styles.spProfileVal} ${styles.spProfileMessage}`} aria-label={kv.value as string} title={kv.value as string}>
                                                                                {(kv.value as string).length > 20 ? `${(kv.value as string).substring(0, 20)}...` : kv.value as string}
                                                                            </StackItem>
                                                                        ) : (
                                                                            <>
                                                                                <StackItem className={styles.spProfileVal} aria-label={kv.key} title={kv.key}>
                                                                                    {kv.key}
                                                                                </StackItem>
                                                                                <StackItem translate="no" className={styles.spProfileVal} aria-label={kv.value as string} title={kv.value as string}>
                                                                                    {(kv.value as string).length > 20 ? `${(kv.value as string).substring(0, 20)}...` : kv.value as string}
                                                                                </StackItem>
                                                                            </>
                                                                        )
                                                                    )}
                                                                </Stack>
                                                            ))
                                                            : null;
                                                    })()}
                                                </Stack>
                                            ))
                                    )
                                }
                                {/* Only render the footer container if there are items to display */}
                                {displayFooterSection() && (
                                    <Stack className={styles.spFooterContainer}>
                                        {!selectedProfileSection &&
                                            (getVisibleFooterItems("Default Actions").map((kv: { key: string; value: string }, itemIdx: number) => (
                                                <Stack
                                                    key={`default-${itemIdx}`}
                                                    horizontal
                                                    horizontalAlign="space-between"
                                                    verticalAlign="center"
                                                    tabIndex={0}
                                                    className={styles.spFooterTitleContainer}
                                                    onClick={() => {
                                                        if (kv.value === "handleCaseSelectDialogOpen") {
                                                            setQuestionV("");
                                                        }
                                                        actionHandlers[kv.value]?.();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!popUpBlocker) {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                if (kv.value === "handleCaseSelectDialogOpen") {
                                                                    setQuestionV("");
                                                                }
                                                                actionHandlers[kv.value]?.();
                                                            }
                                                        }
                                                    }}
                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <StackItem
                                                        className={chatStyles.spSubsectionTitle}
                                                        title={kv.key}
                                                        aria-label={kv.key}
                                                    >
                                                        {kv.key}
                                                    </StackItem>
                                                </Stack>
                                            ))
                                            )}
                                        {selectedProfileSection &&
                                            (getVisibleFooterItems("Secondary Actions").map((kv: { key: string; value: string }, itemIdx: number) => (
                                                <Stack
                                                    key={`secondary-${itemIdx}`}
                                                    horizontal
                                                    horizontalAlign="space-between"
                                                    verticalAlign="center"
                                                    tabIndex={0}
                                                    className={styles.spFooterTitleContainer}
                                                    onClick={() => {
                                                        if (kv.value === "handleCaseSelectDialogOpen") {
                                                            setQuestionV("");
                                                        }
                                                        actionHandlers[kv.value]?.();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!popUpBlocker) {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                if (kv.value === "handleCaseSelectDialogOpen") {
                                                                    setQuestionV("");
                                                                }
                                                                actionHandlers[kv.value]?.();
                                                            }
                                                        }
                                                    }}
                                                    style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <StackItem
                                                        className={chatStyles.spSubsectionTitle}
                                                        title={kv.key}
                                                        aria-label={kv.key}
                                                    >
                                                        {kv.key}
                                                    </StackItem>
                                                </Stack>
                                            ))
                                            )
                                        }
                                    </Stack>
                                )}
                            </div>
                        </SlidingPane>
                    </FocusTrap>
                )}
            </MediaQuery>
        </>
    );
};

export default ProfilePanel;
